import { Button, DataTable, ResultsCard, ResultsToolbar } from 'shared/components/ui';
import React, { useState, useCallback } from 'react';
import { People, UnfoldMore, ArrowBack } from '@mui/icons-material';

import { Search, Clear, Download } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import { Toast } from '../../../shared/components/Toast';
import { personasAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;

const GrillaPacientes = () => {
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [page, setPage] = useState(1);
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
  const [turnos, setTurnos] = useState([]);
  const [turnosLoading, setTurnosLoading] = useState(false);
  const [acordeonAbierto, setAcordeonAbierto] = useState(null);

  const { sortedData, requestSort, getSortIcon } = useSortableTable(pacientes);

  const buscarPacientes = useCallback(async () => {
    if (!buscar.trim()) return;
    setLoading(true);
    try {
      const res = await personasAPI.buscarPacientes(buscar.trim());
      setPacientes(res.data || []);
      setPage(1);
    } catch {
      setToast({ message: 'Error al buscar pacientes.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [buscar]);

  const handleVerDetalle = async (p) => {
    setPacienteSeleccionado(p);
    setTurnos([]);
    setAcordeonAbierto(null);
    setTurnosLoading(true);
    try {
      const res = await personasAPI.getTurnosPorPersona(p.idPersona);
      setTurnos(res.data || []);
    } catch {
      setToast({ message: 'Error al cargar el historial.', type: 'error' });
    } finally {
      setTurnosLoading(false);
    }
  };

  const handleVolver = () => {
    setPacienteSeleccionado(null);
    setTurnos([]);
    setAcordeonAbierto(null);
  };

  const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);
  const paginados = sortedData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const calcEdad = (fechaNac) => {
    if (!fechaNac) return '';
    const hoy = new Date();
    const nac = new Date(fechaNac);
    let edad = hoy.getFullYear() - nac.getFullYear();
    if (hoy.getMonth() < nac.getMonth() || (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())) edad--;
    return edad;
  };

  const handleExportar = () => {
    const p = pacienteSeleccionado;
    const edad = calcEdad(p.fechaNacimiento);

    // Hoja 1: Datos del paciente
    const datosWS = XLSX.utils.aoa_to_sheet([
      ['Apellido y Nombre', p.apeNombre],
      ['DNI', p.dni],
      ['Tipo Documento', p.tipoDni],
      ['Fecha Nacimiento', formatFecha(p.fechaNacimiento)],
      ['Edad', edad],
      ['Celular', p.telCelular],
      ['Correo', p.mail],
      ['Obra Social', p.obraSocial],
      ['Dirección', p.direccion],
      ['Código Postal', p.codigoPostal],
    ]);

    // Hoja 2: Historial de turnos
    const headers = ['#', 'Fecha', 'Tipo Estudio', 'Médico', 'Estado', 'Centro', 'Observación', 'Diagnóstico', 'Realizó'];
    const rows = turnos.map((t, i) => [
      i + 1,
      formatFechaHora(t.inicio),
      t.tipoEstudio,
      t.medico,
      t.estadoTurno,
      t.centro,
      t.observacion,
      t.diagnostico,
      t.realizo === 'S' ? 'Sí' : t.realizo === 'N' ? 'No' : '',
    ]);
    const turnosWS = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, datosWS, 'Datos Paciente');
    XLSX.utils.book_append_sheet(wb, turnosWS, 'Historial');
    XLSX.writeFile(wb, `Historial_${p.apellido}_${p.nombre}.xlsx`);
  };

  const formatFecha = (f) => f ? new Date(f).toLocaleDateString('es-AR') : '';
  const formatFechaHora = (f) => f ? new Date(f).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '';

  if (pacienteSeleccionado) {
    const p = pacienteSeleccionado;
    return (
      <div className="grilla-turnos">
        <Header />
        <div className="main-content">
          <div className="page-header">
            <h2 className="page-title-grilla">
              <People sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
              Historial de Pacientes
            </h2>
          </div>

          <ResultsCard className="results-card" style={{ marginBottom: '1rem' }}>
            <ResultsToolbar className="results-header">
              <h3 className="results-title" style={{ color: '#6d1212' }}>Datos del Paciente</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button variant="success" size="sm" onClick={handleExportar} disabled={turnosLoading}>
                  <Download sx={{ fontSize: 16, mr: 0.5 }} /> Exportar Excel
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={handleVolver}>
                  <ArrowBack sx={{ fontSize: 16, mr: 0.5 }} /> Volver
                </Button>
              </div>
            </ResultsToolbar>
            <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.75rem' }}>
              {[
                ['Tipo Documento', p.tipoDni],
                ['DNI', p.dni],
                ['Apellido', p.apellido],
                ['Nombre', p.nombre],
                ['Fecha Nacimiento', formatFecha(p.fechaNacimiento)],
                ['Celular', p.telCelular],
                ['Obra Social / Prepaga', p.obraSocial],
                ['Correo', p.mail],
                ['Dirección', p.direccion],
                ['Código Postal', p.codigoPostal],
              ].map(([label, value]) => value ? (
                <div key={label}>
                  <span style={{ fontWeight: '600', fontSize: '0.8rem', color: '#64748b' }}>{label}</span>
                  <div style={{ fontSize: '0.9rem' }}>{value}</div>
                </div>
              ) : null)}
            </div>
          </ResultsCard>

          <ResultsCard className="results-card">
            <ResultsToolbar className="results-header">
              <h3 className="results-title" style={{ color: '#6d1212' }}>Consultas / Prácticas</h3>
              {turnosLoading && <span className="loading-indicator">Cargando...</span>}
            </ResultsToolbar>
            <div style={{ padding: '1rem' }}>
              {turnos.length === 0 && !turnosLoading && (
                <p style={{ color: '#64748b', textAlign: 'center' }}>No hay consultas anteriores.</p>
              )}
              {turnos.map((t, i) => (
                <div key={t.idTurno} style={{ border: '1px solid #dee2e6', borderRadius: '6px', marginBottom: '0.5rem' }}>
                  <button
                    onClick={() => setAcordeonAbierto(acordeonAbierto === i ? null : i)}
                    style={{
                      width: '100%', textAlign: 'left', padding: '0.75rem 1rem',
                      background: acordeonAbierto === i ? '#f8f9fa' : 'white',
                      border: 'none', borderRadius: '6px', cursor: 'pointer',
                      fontWeight: '500', fontSize: '0.875rem',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}
                  >
                    <span>
                      {i + 1}) {formatFechaHora(t.inicio)} — {t.tipoEstudio} — {t.estadoTurno} — {t.medico}
                    </span>
                    <span>{acordeonAbierto === i ? '▲' : '▼'}</span>
                  </button>
                  {acordeonAbierto === i && (
                    <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #dee2e6', fontSize: '0.875rem' }}>
                      {t.centro && <p><strong>CENTRO:</strong> {t.centro}</p>}
                      {t.observacion && <p><strong>OBSERVACIÓN:</strong> {t.observacion}</p>}
                      {t.diagnostico && <p><strong>DIAGNÓSTICO:</strong> {t.diagnostico}</p>}
                      {t.realizo && <p><strong>REALIZÓ:</strong> {t.realizo === 'S' ? 'SÍ' : 'NO'}</p>}
                      {t.tipoMedico === 1 && (
                        <>
                          {t.motivoConsulta && <p><strong>MOTIVO CONSULTA:</strong> {t.motivoConsulta}</p>}
                          {t.escolaridad && <p><strong>ESCOLARIDAD:</strong> {t.escolaridad}</p>}
                          {t.antecedentesFamiliares && <p><strong>ANTECEDENTES FAMILIARES:</strong> {t.antecedentesFamiliares}</p>}
                          {t.embarazo && <p><strong>EMBARAZO:</strong> {t.embarazo}</p>}
                          {t.parto && <p><strong>PARTO:</strong> {t.parto}</p>}
                          {t.eg && <p><strong>EG:</strong> {t.eg}</p>}
                          {t.pn && <p><strong>PN:</strong> {t.pn}</p>}
                          {t.apgar && <p><strong>APGAR:</strong> {t.apgar}</p>}
                          {t.fei && <p><strong>FEI:</strong> {t.fei}</p>}
                          {t.internaciones && <p><strong>INTERNACIONES:</strong> {t.internaciones}</p>}
                          {t.sueno && <p><strong>SUEÑO:</strong> {t.sueno}</p>}
                          {t.otrosAntecedentes && <p><strong>OTROS ANTECEDENTES:</strong> {t.otrosAntecedentes}</p>}
                          {t.examenFisico && <p><strong>EXAMEN FÍSICO:</strong> {t.examenFisico}</p>}
                          {t.medicacionRecibe && <p><strong>MEDICACIÓN RECIBIDA:</strong> {t.medicacionRecibe}</p>}
                          {t.examenComplementario && <p><strong>EXÁMENES COMPLEMENTARIOS:</strong> {t.examenComplementario}</p>}
                          {t.diagnosticoPresunto && <p><strong>DIAGNÓSTICO PRESUNTO:</strong> {t.diagnosticoPresunto}</p>}
                          {t.conducta && <p><strong>CONDUCTA:</strong> {t.conducta}</p>}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ResultsCard>
        </div>
        <Footer />
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    );
  }

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <People sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Historial de Pacientes
          </h2>
          <span className="page-subtitle">Búsqueda y consulta de historial de pacientes</span>
        </div>

        <div className="filters-card">
          <div className="filters-header"><h3 className="filters-title">Búsqueda</h3></div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${buscar ? 'filter-active' : ''}`}>
                <label className="filter-label">DNI, Nombre o Apellido</label>
                <input
                  type="text" className="filter-input" placeholder="dni, nombre..."
                  value={buscar}
                  onChange={(e) => setBuscar(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && buscarPacientes()}
                />
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={buscarPacientes} disabled={loading || !buscar.trim()}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={() => { setBuscar(''); setPacientes([]); setPage(1); }}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{pacientes.length} pacientes encontrados</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('idPersona')} style={{ cursor: 'pointer' }}>
                    # {getSortIcon('idPersona') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('apeNombre')} style={{ cursor: 'pointer' }}>
                    Paciente {getSortIcon('apeNombre') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Edad</th>
                  <th onClick={() => requestSort('dni')} style={{ cursor: 'pointer' }}>
                    DNI {getSortIcon('dni') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Teléfono</th>
                  <th onClick={() => requestSort('obraSocial')} style={{ cursor: 'pointer' }}>
                    Obra Social {getSortIcon('obraSocial') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 ? (
                  <tr><td colSpan="7" className="empty-state">
                    {loading ? 'Cargando...' : pacientes.length === 0 ? 'Ingrese un criterio de búsqueda' : 'No hay pacientes para mostrar'}
                  </td></tr>
                ) : paginados.map(p => (
                  <tr key={p.idPersona}>
                    <td>{p.idPersona}</td>
                    <td>{p.apeNombre}</td>
                    <td>{calcEdad(p.fechaNacimiento)}</td>
                    <td>{p.dni}</td>
                    <td>{p.telCelular}</td>
                    <td>{p.obraSocial}</td>
                    <td>
                      <Button variant="outline-primary" size="sm" onClick={() => handleVerDetalle(p)}>Ver</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaPacientes;
